// Minnesotaänger Mini App — pending-op polling (202 → observed result).
// New code (not from telegram-tt). Contract: CHAT_CONTRACT.md §5.1.
//
// Every mutation (send/reroll/edit/delete) is 202 Accepted and processed
// asynchronously by the worker (10–90s). While a chat with pending ops is
// open, this module polls GET messages every ~2.5s and, when a completion
// predicate holds, drops the op and refreshes history so the result becomes
// visible. The poll itself never writes into the message list — the UI only
// renders what loadHistory fetched, so the user's scroll/viewport is never
// clobbered by a background fetch.
//
// Deadline: an op that neither completes nor fails within 120s is dropped
// silently — no fake success, the old bubbles stay exactly as they are.

import type { ApiMiniAppMessage } from '../../../api/miniapp/types';
import { miniappApi } from '../../../api/miniapp/client';
import { getActions, getGlobal, setGlobal } from '../../index';
import { updateMiniapp } from '../../miniapp/update';
import type { MiniAppPendingOperation } from '../../miniapp/types';
import type { GlobalState } from '../../types';

export const POLL_INTERVAL_MS = 2500;
export const PENDING_OP_DEADLINE_MS = 120_000;

// One page of the newest messages is enough to test every completion
// predicate (send reply is newer than the op's tail; reroll/edit/delete
// targets are recent by construction).
const POLL_PAGE_LIMIT = 50;

let pollingTimer: number | undefined;
let pollingInFlight = false;

// --- registration helpers (used by actions/api/messages.ts) ---

export function addPendingOperation(chatId: string, op: MiniAppPendingOperation): void {
  const global = getGlobal();
  setGlobal(updateMiniapp(global, (m) => ({
    ...m,
    pendingOperationsByChatId: {
      ...m.pendingOperationsByChatId,
      [chatId]: [...(m.pendingOperationsByChatId[chatId] ?? []), op],
    },
  })));
  ensurePollingScheduled();
}

export function removePendingOperation(
  chatId: string,
  targetEventId: number,
  kind: MiniAppPendingOperation['kind'],
): void {
  const global = getGlobal();
  const ops = global.miniapp.pendingOperationsByChatId[chatId] ?? [];
  const next = ops.filter((op) => !(op.kind === kind && op.targetEventId === targetEventId));
  if (next.length === ops.length) return;
  setGlobal(updateMiniapp(global, (m) => ({
    ...m,
    pendingOperationsByChatId: next.length
      ? { ...m.pendingOperationsByChatId, [chatId]: next }
      : omitKey(m.pendingOperationsByChatId, chatId),
  })));
}

// Dedup guard (CHAT_CONTRACT §5.2): same chat, same target already has a
// pending op → the new request is ignored. With kind=undefined any op on the
// target blocks (reroll/edit/delete all rewrite the same group).
export function hasPendingOperation(
  global: GlobalState,
  chatId: string,
  targetEventId: number | null,
  kind?: MiniAppPendingOperation['kind'],
): boolean {
  const ops = global.miniapp.pendingOperationsByChatId[chatId] ?? [];
  return ops.some((op) => op.targetEventId === targetEventId && (!kind || op.kind === kind));
}

// --- polling loop ---

export function ensurePollingScheduled(): void {
  if (pollingTimer !== undefined) return;
  pollingTimer = setInterval(pollTick, POLL_INTERVAL_MS);
}

async function pollTick(): Promise<void> {
  if (pollingInFlight) return;
  pollingInFlight = true;
  try {
    await pollOnce();
  } finally {
    pollingInFlight = false;
  }
}

// Exported for tests: runs one poll cycle against the current state.
export async function pollOnce(): Promise<void> {
  const global = getGlobal();
  const { activeChatId } = global.miniapp;

  // Polling only happens while the chat is open; switching away pauses it
  // (reopening re-fetches the truth via loadHistory and the loop resumes).
  if (!activeChatId) return;
  const ops = global.miniapp.pendingOperationsByChatId[activeChatId] ?? [];
  if (ops.length === 0) {
    stopPollingIfIdle();
    return;
  }

  let fetched: ApiMiniAppMessage[];
  try {
    const { messages } = await miniappApi.getMessages(activeChatId, undefined, POLL_PAGE_LIMIT);
    fetched = messages;
  } catch {
    // Transient fetch failure: keep the ops, retry on the next tick.
    return;
  }

  const now = Date.now();
  const completed: MiniAppPendingOperation[] = [];
  const dropped: MiniAppPendingOperation[] = [];
  const remaining: MiniAppPendingOperation[] = [];

  for (const op of ops) {
    if (now - op.startedAt > PENDING_OP_DEADLINE_MS) {
      // Deadline passed: stop tracking silently (§5.1). The op leaves the
      // state (otherwise it would dangle forever and keep the poll loop
      // alive), but no fake success — the old bubbles stay exactly as they
      // are, no reload.
      dropped.push(op);
      continue;
    }
    if (isOpCompleted(op, fetched)) {
      completed.push(op);
    } else {
      remaining.push(op);
    }
  }

  if (completed.length > 0 || dropped.length > 0) {
    setGlobal(updateMiniapp(getGlobal(), (m) => ({
      ...m,
      pendingOperationsByChatId: remaining.length
        ? { ...m.pendingOperationsByChatId, [activeChatId]: remaining }
        : omitKey(m.pendingOperationsByChatId, activeChatId),
    })));
    if (completed.length > 0) {
      cleanupRefsToTargets(activeChatId, completed);
      // One full reload makes the result of every completed op visible.
      getActions().loadHistory({ chatId: activeChatId });
    }
  }

  stopPollingIfIdle();
}

// Completion predicates (CHAT_CONTRACT §5.1):
// - send: an assistant message newer than the op's tail appeared.
// - reroll/edit/delete: the target vanished from the visible list
//   (superseded/tombstoned messages are filtered out server-side). A failed
//   op (409/502) never makes the target disappear → it times out silently and
//   the old bubble stays, so the UI never fakes success.
function isOpCompleted(op: MiniAppPendingOperation, messages: ApiMiniAppMessage[]): boolean {
  if (op.kind === 'send') {
    return messages.some(
      (m) => m.role === 'assistant' && m.visibility === 'visible' && m.event_id > op.tailEventId,
    );
  }
  return !messages.some((m) => m.event_id === op.targetEventId);
}

// Reply/selection references to messages that disappeared (edit/reroll/delete
// completed) must not dangle (CHAT_CONTRACT §7).
function cleanupRefsToTargets(chatId: string, completed: MiniAppPendingOperation[]): void {
  const targets = new Set(
    completed.filter((op) => op.targetEventId !== null).map((op) => op.targetEventId as number),
  );
  if (targets.size === 0) return;

  const global = getGlobal();
  setGlobal(updateMiniapp(global, (m) => {
    // v1 (fix-list E): reply target is a number event_id now — direct lookup.
    const replyingTo = m.replyingToEventIdByChatId[chatId];
    const selectedIds = m.selectedMessageIdsByChatId[chatId] ?? [];
    const nextSelected = selectedIds.filter((id) => !targets.has(Number(id)));
    const clearReply = replyingTo !== undefined && targets.has(replyingTo);
    const clearSelection = nextSelected.length !== selectedIds.length;
    if (!clearReply && !clearSelection) return m;
    return {
      ...m,
      replyingToEventIdByChatId: clearReply ? omitKey(m.replyingToEventIdByChatId, chatId) : m.replyingToEventIdByChatId,
      selectedMessageIdsByChatId: clearSelection
        ? { ...m.selectedMessageIdsByChatId, [chatId]: nextSelected }
        : m.selectedMessageIdsByChatId,
    };
  }));
}

function stopPollingIfIdle(): void {
  const global = getGlobal();
  const hasAnyPending = Object.values(global.miniapp.pendingOperationsByChatId)
    .some((ops) => ops.length > 0);
  if (hasAnyPending || pollingTimer === undefined) return;
  clearInterval(pollingTimer);
  pollingTimer = undefined;
}

function omitKey<K extends string, V>(record: Record<K, V>, key: K): Record<K, V> {
  const { [key]: _removed, ...rest } = record;
  return rest as Record<K, V>;
}
